import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '@/components/layout/Layout';
import SEOHead, { getBreadcrumbSchema } from '@/components/shared/SEOHead';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { projectsData, companyInfo } from '@/data/mock';
import { 
  ArrowRight, CheckCircle2, Phone, ChevronRight, MapPin, Calendar, 
  DollarSign, Users, Wrench, Target, Lightbulb, Clock, Leaf, Heart,
  AlertTriangle, CheckCheck
} from 'lucide-react';

const getProjectSchema = (project) => ({
  "@context": "https://schema.org",
  "@type": "Project",
  "name": project.title,
  "description": project.summary,
  "image": project.heroImage,
  "url": `https://redspire.com/projects/${project.slug}`,
  "location": {
    "@type": "Place",
    "name": project.location
  },
  "performer": {
    "@type": "Organization",
    "name": companyInfo.name
  }
});

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const project = projectsData[projectId];

  if (!project) {
    return <Navigate to="/projects" replace />;
  }

  const breadcrumbItems = [
    { name: 'Home', url: 'https://redspire.com' },
    { name: 'Projects', url: 'https://redspire.com/projects' },
    { name: project.title, url: `https://redspire.com/projects/${project.slug}` }
  ];

  const combinedSchema = [
    getProjectSchema(project),
    getBreadcrumbSchema(breadcrumbItems)
  ];

  return (
    <Layout>
      <SEOHead
        title={project.seo.title}
        description={project.seo.description}
        keywords={project.seo.keywords}
        image={project.heroImage}
        url={`https://redspire.com/projects/${project.slug}`}
        schema={combinedSchema}
      />

      {/* Hero Section */}
      <section className="relative h-[60vh] min-h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={project.heroImage}
            alt={`${project.title} - Infrastructure project`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 via-gray-900/70 to-gray-900/40" />
        </div>
        <div className="relative h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
          <div className="max-w-2xl">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm text-gray-300 mb-6">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/projects" className="hover:text-white">Projects</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-red-400">{project.title}</span>
            </nav>
            <div className="flex gap-3 mb-4">
              <Badge className={project.status === 'Completed' ? 'bg-green-600' : 'bg-amber-600'}>
                {project.status}
              </Badge>
              <Badge variant="secondary" className="bg-white/20 text-white">
                {project.category}
              </Badge>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
              {project.title}
            </h1>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-red-600" />
      </section>

      {/* Project Quick Facts */}
      <section className="py-8 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="flex items-center gap-3">
              <MapPin className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-gray-400 uppercase">Location</p>
                <p className="font-medium">{project.location}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Users className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-gray-400 uppercase">Client</p>
                <p className="font-medium text-sm">{project.client}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <DollarSign className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-gray-400 uppercase">Value</p>
                <p className="font-medium">{project.value}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-red-500" />
              <div>
                <p className="text-xs text-gray-400 uppercase">Duration</p>
                <p className="font-medium">{project.duration}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Summary */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Project Overview</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {project.summary}
              </p>
            </div>
            <div>
              <Card className="border-0 shadow-lg bg-gray-50 sticky top-24">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Discuss Your Project</h3>
                  <p className="text-gray-600 text-sm mb-4">
                    Have a similar project in mind? Our team is ready to help.
                  </p>
                  <a href={`tel:${companyInfo.phone}`} className="flex items-center gap-2 text-red-600 font-medium mb-4">
                    <Phone className="w-4 h-4" />
                    {companyInfo.phone}
                  </a>
                  <Link to="/contact">
                    <Button className="w-full bg-red-600 hover:bg-red-700 text-white">
                      Start a Conversation
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Scope of Work */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Target className="w-6 h-6 text-red-600" />
                <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Scope</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Scope of Work</h2>
              <ul className="space-y-4">
                {project.scopeOfWork.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src={project.images[0]?.src}
                alt={project.images[0]?.alt || `${project.title} construction`}
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
              <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-red-600 rounded-xl -z-10" />
            </div>
          </div>
        </div>
      </section>

      {/* Technical Approach */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Lightbulb className="w-6 h-6 text-red-500" />
                <span className="text-red-500 font-semibold text-sm uppercase tracking-wider">Methodology</span>
              </div>
              <h2 className="text-3xl font-bold mb-6">Technical Approach</h2>
              <p className="text-lg text-gray-300 leading-relaxed">
                {project.technicalApproach}
              </p>
            </div>
            <div className="relative">
              <img
                src={project.images[1]?.src || project.heroImage}
                alt={project.images[1]?.alt || `${project.title} technical work`}
                className="rounded-2xl shadow-xl w-full aspect-square object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Challenges & Solutions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Problem Solving</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Key Challenges & Solutions</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {project.challenges.map((item, index) => (
              <Card key={index} className="border-0 shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                <div className="h-1 bg-red-600" />
                <CardContent className="p-8">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                    {item.challenge}
                  </h3>
                  <p className="text-gray-600 flex items-start gap-2">
                    <CheckCheck className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    {item.solution}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Timeline */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Clock className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Schedule</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Project Timeline</h2>
          </div>
          <div className="relative">
            <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-red-200 -translate-x-1/2" />
            <div className="space-y-8">
              {project.timeline.map((phase, index) => (
                <div
                  key={index}
                  className={`flex flex-col md:flex-row items-center gap-6 ${index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  <div className={`flex-1 ${index % 2 === 0 ? 'md:text-right' : 'md:text-left'}`}>
                    <Card className="border-0 shadow-md inline-block">
                      <CardContent className="p-6">
                        <Badge className="bg-red-600 hover:bg-red-700 mb-2">{phase.period}</Badge>
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">{phase.phase}</h3>
                        <p className="text-gray-600 text-sm">{phase.description}</p>
                      </CardContent>
                    </Card>
                  </div>
                  <div className="relative z-10">
                    <div className="w-4 h-4 rounded-full bg-red-600 border-4 border-white shadow" />
                  </div>
                  <div className="flex-1" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Materials & Technologies */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Wrench className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Technology</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Materials & Methods</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {project.materials.map((material, index) => (
              <Badge
                key={index}
                variant="secondary"
                className="px-4 py-2 text-sm bg-gray-100 text-gray-700 hover:bg-red-50 hover:text-red-700 transition-colors"
              >
                {material}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Sustainability */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <Leaf className="w-6 h-6 text-green-600" />
                <span className="text-green-600 font-semibold text-sm uppercase tracking-wider">Sustainability</span>
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Sustainability & Compliance</h2>
              <ul className="space-y-4">
                {project.sustainability.map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <img
                src={project.images[2]?.src || project.images[0]?.src}
                alt={project.images[2]?.alt || `${project.title} sustainability features`}
                className="rounded-2xl shadow-xl w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Community Impact */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="flex items-center justify-center gap-3 mb-4">
              <Heart className="w-6 h-6 text-red-600" />
              <span className="text-red-600 font-semibold text-sm uppercase tracking-wider">Impact</span>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Community Impact</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              {project.communityImpact}
            </p>
          </div>
        </div>
      </section>

      {/* Project Gallery */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Project Gallery</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {project.images.map((image, index) => (
              <div key={index} className="aspect-square rounded-xl overflow-hidden shadow-lg">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-red-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Start Your Infrastructure Project?
          </h2>
          <p className="text-lg text-red-100 max-w-2xl mx-auto mb-10">
            Contact Redspire today to discuss how our expertise in {project.category.toLowerCase()} 
            projects can support your infrastructure goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-white text-red-600 hover:bg-gray-100 px-8 group">
                Request a Proposal
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
            <Link to="/projects">
              <Button size="lg" variant="outline" className="border-2 border-white text-white bg-transparent hover:bg-white/10 px-8">
                View More Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default ProjectDetailPage;
